import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import Joyride from 'react-joyride';
import { customJoyrideStyles, pulseAnimationCSS, customJoyrideLocale } from '../../constants/joyrideStyles';
import { getRouteTour, getTourSessionKey } from '../../constants/routeTours';

class RouteTourManager extends Component {
  constructor(props) {
    super(props);
    this.state = {
      runTour: false,
      tourSteps: [],
      tourTitle: '',
      currentTourKey: null,
      stepIndex: 0, // Track current step
    };
  }

  componentDidMount() {
    this.checkAndStartTour();
  }

  componentDidUpdate(prevProps) {
    // Check for route changes
    if (prevProps.location.pathname !== this.props.location.pathname) {
      // Clear tour state when route changes
      this.setState({
        runTour: false,
        tourSteps: [],
        tourTitle: '',
        currentTourKey: null,
        stepIndex: 0,
      });
      // Check for new tour on the new route
      this.checkAndStartTour();
    }
  }

  checkAndStartTour = (retryCount = 0) => {
    const { pathname } = this.props.location;
    const { locationSettings } = this.props;
    const maxRetries = 10;
    const retryDelay = 200;
    
    const routeTour = getRouteTour(pathname);
    
    console.log('RouteTourManager - Current pathname:', pathname);
    console.log('RouteTourManager - Found tour:', routeTour);
    console.log('RouteTourManager - Location settings:', locationSettings);
    
    if (!routeTour) {
      console.log('RouteTourManager - No tour found for pathname:', pathname);
      return;
    }

    const sessionKey = getTourSessionKey(routeTour.key);
    const hasSeenTour = sessionStorage.getItem(sessionKey);
    
    console.log('RouteTourManager - Session key:', sessionKey);
    console.log('RouteTourManager - Has seen tour:', hasSeenTour);

    if (!hasSeenTour) {
      setTimeout(() => {
        const validSteps = routeTour.steps.filter(step => {
          const element = document.querySelector(step.target);
          console.log('RouteTourManager - Checking element:', step.target, 'exists:', !!element);
          if (!element) return false;
          if (step.requiresFeature) {
            const featureEnabled = locationSettings && locationSettings[step.requiresFeature] === 1;
            console.log('RouteTourManager - Feature check:', step.requiresFeature, 'enabled:', featureEnabled);
            return featureEnabled;
          }
          return true;
        });
        console.log('RouteTourManager - Valid steps:', validSteps.length);
        if (validSteps.length > 0) {
          console.log('RouteTourManager - Starting tour with beacons');
          // Use Joyride's built-in beacon functionality
          this.setState({
            runTour: false, // Don't start automatically - show beacons only
            tourSteps: validSteps,
            tourTitle: routeTour.title,
            currentTourKey: routeTour.key,
            stepIndex: 0,
          });
        } else if (retryCount < maxRetries) {
          console.log('RouteTourManager - No valid steps found, retrying', retryCount + 1);
          this.checkAndStartTour(retryCount + 1);
        } else {
          console.log('RouteTourManager - No valid steps found after retries');
        }
      }, retryCount === 0 ? 1000 : retryDelay);
    } else {
      console.log('RouteTourManager - Tour already seen, skipping');
    }
  };

  handleTourCallback = (data) => {
    const { status, action } = data;
    
    console.log('RouteTourManager - Tour callback:', data);
    
    if (status === "finished" || status === "skipped" || action === "close") {
      // Mark tour as completed
      const sessionKey = getTourSessionKey(this.state.currentTourKey);
      sessionStorage.setItem(sessionKey, 'true');
      
      this.setState({
        runTour: false,
        tourSteps: [],
        tourTitle: '',
        currentTourKey: null,
      });
    }
  };

  // Method to manually start tour for current route (for testing)
  startCurrentRouteTour = () => {
    const { pathname } = this.props.location;
    const { locationSettings } = this.props;
    const routeTour = getRouteTour(pathname);
    
    if (routeTour) {
      // Remove session storage to allow tour to run
      const sessionKey = getTourSessionKey(routeTour.key);
      sessionStorage.removeItem(sessionKey);
      
      // Filter out steps where target elements don't exist and check location settings
      const validSteps = routeTour.steps.filter(step => {
        const element = document.querySelector(step.target);
        if (!element) return false;
        
        // Check location-specific feature flags
        if (step.requiresFeature) {
          return locationSettings && locationSettings[step.requiresFeature] === 1;
        }
        
        return true;
      });

      if (validSteps.length > 0) {
        this.setState({
          runTour: true,
          tourSteps: validSteps,
          tourTitle: routeTour.title,
          currentTourKey: routeTour.key,
        });
      }
    }
  };

  // Custom beacon styled to match Joyride
  renderBeacon = () => {
    const { tourSteps, currentTourKey, runTour } = this.state;
    if (!tourSteps.length || !currentTourKey || runTour) return null;
    const firstStep = tourSteps[0];
    const element = document.querySelector(firstStep.target);
    if (!element) return null;
    const rect = element.getBoundingClientRect();
    return (
      <>
        <style>{pulseAnimationCSS}</style>
        <div
          className="react-joyride__beacon"
          style={{
            position: 'fixed',
            top: rect.top + rect.height / 2 - 14,
            left: rect.left + rect.width / 2 - 14,
            width: 28,
            height: 28,
            zIndex: 9999,
            cursor: 'pointer',
            animation: 'pulse 2s infinite',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => this.setState({ runTour: true })}
          title="Click to start tour"
        />
      </>
    );
  };

  render() {
    const { runTour, tourSteps, tourTitle } = this.state;
    if (tourSteps.length === 0) {
      return null;
    }
    return (
      <>
        {/* CSS for pulse animation */}
        <style>{pulseAnimationCSS}</style>
        {/* Route-specific tour */}
        <Joyride
          steps={tourSteps}
          run={runTour}
          continuous
          showProgress
          showSkipButton
          hideBackButton
          callback={this.handleTourCallback}
          styles={customJoyrideStyles}
          locale={customJoyrideLocale}
          title={tourTitle}
        />
        {this.renderBeacon()}
      </>
    );
  }
}

const mapStateToProps = ({ appReducer }) => ({
  locationSettings: appReducer.location.settings,
});

export default connect(mapStateToProps)(withRouter(RouteTourManager)); 